// ./01 - PROJETOS DEV/meu-controle-de-estoque/src/components/modals/AddExpensePaymentModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { formatCurrency } from '../../utils/formatters';

const AddExpensePaymentModal = ({ isOpen, onClose, onSave, purchase }) => {
    const [amount, setAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Pix');
    const [selectedInstallments, setSelectedInstallments] = useState(new Set());
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

    const { totalPaid, remainingAmount, pendingInstallments } = useMemo(() => {
        if (!purchase) return { totalPaid: 0, remainingAmount: 0, pendingInstallments: [] };
        
        const paid = purchase.installments?.reduce((acc, inst) => acc + (inst.paidAmount || 0), 0) || 0;
        const remaining = (purchase.totalAmount || 0) - paid;
        const pending = purchase.installments?.filter(inst => inst.status === 'Pendente') || [];
        
        return { totalPaid: paid, remainingAmount: remaining, pendingInstallments: pending };
    }, [purchase]);

    useEffect(() => {
        if (isOpen) {
            setAmount(remainingAmount > 0 ? remainingAmount.toFixed(2) : '');
            setPaymentMethod('Pix'); // Reset para o padrão
            setSelectedInstallments(new Set());
            setPaymentDate(new Date().toISOString().split('T')[0]); // Padrão: Hoje
        }
    }, [isOpen, remainingAmount]);

    useEffect(() => {
        if (selectedInstallments.size > 0) {
            const sumOfSelected = pendingInstallments
                .filter(inst => selectedInstallments.has(inst.installmentNumber))
                .reduce((acc, inst) => {
                    const amountOwed = inst.amount - (inst.paidAmount || 0);
                    return acc + amountOwed;
                }, 0);
            setAmount(sumOfSelected.toFixed(2));
        } else if (document.activeElement?.id !== 'expensePaymentAmount') {
            setAmount('');
        }
    }, [selectedInstallments, pendingInstallments]);

    if (!isOpen || !purchase) return null;
    
    const handleInstallmentSelection = (installmentNumber) => {
        setSelectedInstallments(prev => {
            const newSelection = new Set(prev);
            if (newSelection.has(installmentNumber)) {
                newSelection.delete(installmentNumber);
            } else {
                newSelection.add(installmentNumber);
            }
            return newSelection;
        });
    };

    const handleAmountChange = (e) => {
        setAmount(e.target.value);
        if (selectedInstallments.size > 0) {
            setSelectedInstallments(new Set());
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const paymentAmount = parseFloat(amount);
        
        if (isNaN(paymentAmount) || paymentAmount <= 0) {
            alert('Por favor, insira um valor de pagamento válido.');
            return;
        }
        
        if (paymentAmount > remainingAmount + 0.01) { 
            alert('O valor do pagamento não pode ser maior que o saldo devedor.');
            return;
        }
        
        // Correção do fuso horário utilizando T12:00:00
        const dateOfPayment = new Date(`${paymentDate}T12:00:00`);
        onSave(purchase.id, paymentAmount, paymentMethod, dateOfPayment);
        onClose();
    };

    return (
        <div className="modal-overlay z-50 flex items-center justify-center fixed inset-0 bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-lg m-4 sm:m-8 max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Registrar Pagamento</h2>
                <p className="text-gray-600 mb-4">Compromisso: {purchase.description}</p>
                
                <div className="space-y-2 text-sm mb-6 bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between"><span className="font-medium text-gray-600">Valor Total:</span><span className="font-semibold">{formatCurrency(purchase.totalAmount)}</span></div>
                    <div className="flex justify-between"><span className="font-medium text-gray-600">Valor Já Pago:</span><span className="font-semibold text-green-600">{formatCurrency(totalPaid)}</span></div>
                    <div className="flex justify-between border-t pt-2 mt-2"><span className="font-bold text-gray-800">Saldo Devedor:</span><span className="font-bold text-red-600">{formatCurrency(remainingAmount)}</span></div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {pendingInstallments.length > 0 && (
                        <div>
                            <label className="form-label">Selecionar Parcelas a Pagar</label>
                            <div className="space-y-2 border rounded-lg p-3 max-h-32 overflow-y-auto">
                                {pendingInstallments.map(inst => {
                                    const amountOwed = inst.amount - (inst.paidAmount || 0);
                                    return (
                                        <div key={inst.installmentNumber} className="flex items-center justify-between hover:bg-gray-50 p-1 rounded">
                                            <div className="flex items-center">
                                                <input 
                                                    type="checkbox" 
                                                    id={`expense-inst-${inst.installmentNumber}`}
                                                    checked={selectedInstallments.has(inst.installmentNumber)}
                                                    onChange={() => handleInstallmentSelection(inst.installmentNumber)}
                                                    className="h-4 w-4 rounded border-gray-300"
                                                />
                                                <label htmlFor={`expense-inst-${inst.installmentNumber}`} className="ml-3 text-sm text-gray-700 cursor-pointer">
                                                    Parcela {inst.installmentNumber} ({new Date(inst.dueDate).toLocaleDateString('pt-BR')})
                                                </label>
                                            </div>
                                            <span className="font-medium">{formatCurrency(amountOwed)}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="expensePaymentDate" className="form-label">Data do Pagamento</label>
                            <input 
                                id="expensePaymentDate" 
                                type="date" 
                                value={paymentDate} 
                                onChange={e => setPaymentDate(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]" 
                                required 
                            />
                        </div>

                        <div>
                            <label htmlFor="expensePaymentMethod" className="form-label">Forma de Pagamento</label>
                            <select 
                                id="expensePaymentMethod"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]"
                            >
                                <option value="Pix">Pix</option>
                                <option value="Cartão de Crédito">Cartão de Crédito</option>
                                <option value="Boleto">Boleto</option>
                                <option value="Dinheiro">Dinheiro</option>
                                <option value="Transferência">Transferência</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="expensePaymentAmount" className="form-label">Valor do Pagamento (R$)</label>
                        <input 
                            id="expensePaymentAmount" 
                            value={amount} 
                            onChange={handleAmountChange} 
                            type="number" 
                            step="0.01" 
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]" 
                            required 
                        />
                    </div>
                    <div className="flex flex-col-reverse sm:flex-row justify-end gap-4 pt-4 border-t mt-4">
                        <button type="button" onClick={onClose} className="py-2 px-6 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 w-full sm:w-auto">Cancelar</button>
                        <button type="submit" className="custom-button py-2 px-6 font-semibold rounded-lg w-full sm:w-auto">Confirmar Pagamento</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddExpensePaymentModal;